import { z } from 'zod';
import { CountryCodeSchema, CurrencyCodeSchema, EmailSchema, IsoTimestampSchema, LanguageCodeSchema, PhoneE164Schema, UuidSchema, } from './primitives.js';
import { KycStatusSchema, KycTierSchema, PlanSchema, RiskLevelSchema, UserRoleSchema, UserStatusSchema, } from './enums.js';
/**
 * Public-facing User entity. Mirrors the `users` table in apps/api/prisma/schema.prisma.
 *
 * Compliance fields (kyc_status, kyc_last_review_at, risk_level, pep_status,
 * sanctions_screening_at) are required at the type level even before the
 * underlying flows are implemented — see COMPLIANCE-PLAYBOOK §3.4.
 *
 * password_hash is deliberately NOT in this schema — it never leaves the
 * server and is never serialized.
 */
export const UserSchema = z.object({
    id: UuidSchema,
    email: EmailSchema,
    phone: PhoneE164Schema.nullable(),
    first_name: z.string().min(1).max(100),
    last_name: z.string().min(1).max(100),
    date_of_birth: z.string().date(),
    country_residence: CountryCodeSchema,
    preferred_language: LanguageCodeSchema,
    preferred_currency: CurrencyCodeSchema.default('EUR'),
    role: UserRoleSchema,
    status: UserStatusSchema,
    plan: PlanSchema,
    email_verified_at: IsoTimestampSchema.nullable(),
    two_factor_enabled: z.boolean(),
    // Compliance fields (COMPLIANCE-PLAYBOOK §3.4)
    kyc_status: KycStatusSchema,
    kyc_tier: KycTierSchema,
    kyc_last_review_at: IsoTimestampSchema.nullable(),
    risk_level: RiskLevelSchema,
    pep_status: z.boolean(),
    sanctions_screening_at: IsoTimestampSchema.nullable(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
    deleted_at: IsoTimestampSchema.nullable(),
});
/** Shape returned by the API to the authenticated user themselves. */
export const SelfUserSchema = UserSchema.pick({
    id: true,
    email: true,
    first_name: true,
    last_name: true,
    preferred_language: true,
    preferred_currency: true,
    role: true,
    status: true,
    plan: true,
    two_factor_enabled: true,
    kyc_status: true,
    kyc_tier: true,
    created_at: true,
});
/** Input for user signup (Phase 2). */
export const UserSignupInputSchema = z.object({
    email: EmailSchema,
    password: z.string().min(12).max(128),
    preferred_language: LanguageCodeSchema,
    country_residence: CountryCodeSchema,
    accept_terms: z.literal(true),
});
//# sourceMappingURL=user.js.map